import { ClipboardList, Users, PieChart, X, ExternalLink } from "lucide-react";
import { Section, money } from "@/mockups/dc";
import { C } from "./ExecCharts";

const Bar = ({ v, max }) => <div style={{ height: 6, background: C.grid, borderRadius: 3, marginTop: 4 }}><div style={{ width: `${max ? (v / max) * 100 : 0}%`, height: 6, background: C.teal, borderRadius: 3 }} /></div>;
const REASON = { job_application: "Job application", general_inquiry: "General inquiry", spam: "Spam", vendor: "Vendor", wrong_number: "Wrong number", other: "Other", unspecified: "No reason given" };
const STATUS = { new: "New", contacted: "Contacted", qualified: "Qualified", converted: "Converted", existing_customer: "Existing customer", lost: "Lost", closed_no_response: "No response", not_a_lead: "Not a lead" };

export const ExecServices = ({ services, onPick }) => {
  const total = services.reduce((a, s) => a + s.count, 0);
  return (
    <Section title="Services requested" sub="What leads ask for most, and how often each one books. Click a service to see its leads." icon={ClipboardList} testid="lx-services">
      {!services.length && <div className="muted" style={{ fontSize: 13 }}>No leads in this period.</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {services.map((s) => (
          <button key={s.service} onClick={() => onPick({ service: s.service, title: s.service, n: s.count })} style={{ background: "none", border: 0, padding: 0, textAlign: "left", cursor: "pointer" }} data-testid={`lx-service-${s.service.replace(/\W+/g, "-").toLowerCase()}`}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, gap: 8 }}>
              <span style={{ fontWeight: 600 }}>{s.service}</span>
              <span className="mono"><b>{s.count}</b> <span className="muted">· {Math.round((s.count / total) * 100)}% of requests · {Math.round((s.won / s.count) * 100)}% won</span></span>
            </div>
            <Bar v={s.count} max={services[0].count} />
          </button>
        ))}
      </div>
    </Section>
  );
};

export const ExecOutcomes = ({ o }) => {
  const rows = [["Won", o.won], ["Lost", o.lost], ["No response", o.no_response], ["Still open", o.open]];
  const max = Math.max(...rows.map((r) => r[1]), 1);
  return (
    <Section title="Outcomes" sub="Where this period's leads ended up. Lost reasons aren't tracked yet; not-a-lead reasons are shown below." icon={PieChart} testid="lx-outcomes">
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rows.map(([l, n]) => <div key={l}><div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span>{l}</span><b className="mono">{n}</b></div><Bar v={n} max={max} /></div>)}
        {o.not_a_lead_reasons.length > 0 && (
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }} data-testid="lx-notlead-reasons">
            Not a lead: {o.not_a_lead_reasons.map(([k, n]) => `${REASON[k] || k} ${n}`).join(" · ")}
          </div>
        )}
      </div>
    </Section>
  );
};

const num = (v, unit) => (v === null || v === undefined ? "—" : `${v}${unit}`);

export const ExecReps = ({ reps, showRevenue }) => (
  <Section title="Follow-up productivity" sub="Per person, from actions logged on this period's leads. First reply = median time from lead in to their first action; turnaround = median time between follow-ups." icon={Users} testid="lx-reps">
    {!reps.length && <div className="muted" style={{ fontSize: 13 }}>No follow-up actions logged in this period.</div>}
    {reps.length > 0 && (
      <div style={{ overflowX: "auto" }}>
        <table className="table" style={{ width: "100%" }}>
          <thead><tr>{["Person", "Leads", "Actions", "First reply", "Turnaround", "Won", "Win %", ...(showRevenue ? ["Revenue"] : [])].map((h) => <th key={h} style={{ textAlign: "left" }}>{h}</th>)}</tr></thead>
          <tbody>
            {reps.map((r, i) => (
              <tr key={r.email} style={{ background: i % 2 ? "#F5F9F9" : undefined }} data-testid={`lx-rep-${i}`}>
                <td style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{r.name}</td>
                <td className="mono">{r.leads}</td>
                <td className="mono">{r.actions}</td>
                <td className="mono" style={{ color: r.first_reply_min > 60 ? C.neg : undefined }}>{r.first_reply_min === null ? "—" : r.first_reply_min >= 120 ? `${Math.round(r.first_reply_min / 60)}h` : `${r.first_reply_min}m`}</td>
                <td className="mono">{num(r.turnaround_h, "h")}</td>
                <td className="mono">{r.won}</td>
                <td className="mono">{r.win_pct}%</td>
                {showRevenue && <td className="mono" style={{ fontWeight: 700 }}>{money(r.revenue)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Section>
);

const drillSub = (data, won) => {
  if (!data) return "Loading…";
  const rev = won ? ` · ${money(data.leads.reduce((a, r) => a + (r.revenue || 0), 0))} revenue, highest first` : "";
  const more = data.total > data.leads.length ? ` · showing ${won ? "top" : "latest"} ${data.leads.length}` : "";
  return `${data.total} leads${rev}${more}. Click a name to open the lead in a new tab.`;
};

export const ExecDrill = ({ pick, data, loading, onClose, won = pick.stage === "won" }) => (
  <Section title={`Leads · ${pick.title}`} sub={loading ? "Loading…" : drillSub(data, won)} icon={ClipboardList} testid="lx-drill"
    right={<button className="btn btn-sm" onClick={onClose} data-testid="lx-drill-close"><X size={14} />Close</button>}>
    {!loading && data && (
      <div style={{ overflowX: "auto" }}>
        <table className="table" style={{ width: "100%" }}>
          <thead><tr>{["Lead", "Status", "Source", "Service", "Last action by", "Age", ...(won ? ["Revenue"] : [])].map((h) => <th key={h} style={{ textAlign: "left" }}>{h}</th>)}</tr></thead>
          <tbody>
            {data.leads.map((r, i) => (
              <tr key={r.id} style={{ background: i % 2 ? "#F5F9F9" : undefined }} data-testid={`lx-drill-row-${i}`}>
                <td style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                  <a href={`/leads/${r.id}`} target="_blank" rel="noreferrer" style={{ color: C.teal, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`lx-drill-link-${i}`}>{r.name}<ExternalLink size={12} /></a>
                </td>
                <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>{STATUS[r.status] || r.status}</td>
                <td className="muted" style={{ fontSize: 12 }}>{r.source || "—"}</td>
                <td style={{ fontSize: 12 }}>{r.service || "—"}</td>
                <td style={{ fontSize: 12 }}>{r.last_by || "—"}</td>
                <td className="mono">{r.age_d === null ? "—" : `${r.age_d}d`}</td>
                {won && <td className="mono" style={{ fontWeight: 700, color: r.revenue ? C.teal : undefined }} data-testid={`lx-drill-rev-${i}`}>{r.revenue ? money(r.revenue) : "—"}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Section>
);
